import { MyProjects } from '@platform/client';
import {
  ProCard,
  ProCardProject,
  ProjectPublishingStage,
  ProSavedProjectCard,
} from '@platform/client/schemas';
import { AlertIconTooltip, Card, CardBadge } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useSearch } from 'wouter';
import { ProjectType } from '.';
import dataTestids from '../../constants/test-ids';
import { DiscoveryCallUpdateModalContext } from './Modals/DiscoveryCallUpdateModal';
import { IntroductionModalContext } from './Modals/IntroductionModal';
import { UpdateProjectDatesModalContext } from './Modals/UpdateProjectDatesModal';
import {
  formatDateStr,
  getButtonProps,
  getCardStatusBadge,
  getHeaderContent,
  getSubtext,
} from './utils';

export const ProUserCard = ({ cardData }: { cardData: ProCard }) => {
  const { cardStatus, project, organization } = cardData;

  const { openIntroductionModal } = useContext(IntroductionModalContext);
  const { openDiscoveryCallUpdateModal } = useContext(DiscoveryCallUpdateModalContext);
  const { openUpdateProjectDatesModal } = useContext(UpdateProjectDatesModalContext);

  // Open modal if introductionId is in the URL param
  const searchParams = useSearch();
  const urlSearchParams = new URLSearchParams(searchParams);
  const paramIntroId = parseInt(urlSearchParams.get('introductionId') ?? '');
  useEffect(() => {
    if (paramIntroId && paramIntroId === cardData.introduction.id) {
      openIntroductionModal({
        userType: 'experteer',
        cardData: cardData,
        project: project,
      });
    }
  }, [paramIntroId, cardData, openIntroductionModal, project]);

  const headerProps = getHeaderContent({ cardData, userType: 'pro' });
  const badges: CardBadge[] = [getCardStatusBadge(cardStatus)];
  if (project?.roleType) {
    badges.unshift({
      text: ProjectType[project.roleType],
      color: 'secondary-brand-25',
    });
  } else {
    badges.unshift({
      text: 'Custom',
      color: 'secondary-brand-25',
    });
  }
  const subText = getSubtext({ cardData, userType: 'pro' });

  const buttonProps = getButtonProps({ cardData, userType: 'pro' });
  let buttonCta: (() => void) | undefined = undefined;

  switch (buttonProps?.modal) {
    case 'introduction':
      buttonCta = () => {
        openIntroductionModal({
          userType: 'experteer',
          cardData: cardData,
          project: project,
        });
      };
      break;

    case 'discovery':
      buttonCta = () => {
        openDiscoveryCallUpdateModal({
          userType: 'experteer',
          cardData: cardData,
          project: project,
        });
      };
      break;

    case 'projectDates':
      buttonCta = () => {
        if (project) {
          openUpdateProjectDatesModal({
            userType: 'experteer',
            isTeam: false,
            organizationName: organization.name,
            id: project.id,
            startDate: new Date(project.startDate ?? new Date()),
            endDate: new Date(project.endDate ?? new Date()),
          });
        }
      };
      break;

    case 'email':
      buttonCta = () => {
        window.open(
          `mailto:match@movingworlds.org?subject=Update on match - project #${project?.id}`,
        );
      };
      break;

    default:
      buttonCta = undefined;
      break;
  }

  return (
    <Card
      headerContent={headerProps.headerText}
      headerColor={headerProps.headerColor}
      buttonText={buttonProps?.buttonText}
      buttonVariant={buttonProps?.buttonVariant}
      buttonOnClick={buttonCta}
      content={CardContent(project)}
      contentTextHref={project?.id ? `/platform/directory/projects/${project.id}` : undefined}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      avatarSize='md'
      avatarHref={cardData.avatar || ''}
      badges={badges}
      variant='large'
      footerText={subText}
      userOrOrg='organization'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project?.id ?? 'custom-' + organization.id}`}
    />
  );
};

export const ProUserSavedProjectCard = ({ cardData }: { cardData: ProSavedProjectCard }) => {
  const { project, organization } = cardData;
  const queryClient = useQueryClient();
  const published = project.publishingStage === ProjectPublishingStage.published;
  // Setting projectUrl to undefined will properly set button to disabled since defined Href will create a link as button
  const projectUrl = published ? `/platform/directory/projects/${project.id}` : undefined;

  const unsaveProjectMutation = MyProjects.useUnsaveProject({
    mutation: {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
      },
    },
  });

  return (
    <Card
      headerContent={undefined}
      headerColor={'gray-50'}
      buttonText={published ? 'See Details' : 'Unavailable'}
      buttonVariant={'secondary-gray'}
      buttonHref={projectUrl}
      buttonDisabled={!published}
      content={project.name}
      contentTextHref={projectUrl}
      contentSubText={organization.name}
      contentSubtextHref={`/platform/directory/organizations/${organization.id}`}
      avatarSize='md'
      avatarHref={cardData.avatar || ''}
      badges={[
        {
          text: ProjectType[project.roleType],
          color: 'secondary-brand-25',
        },
      ]}
      variant='large'
      footerText={`Saved ${formatDateStr(cardData.savedAt)}`}
      footerLinkText={'Unsave'}
      footerLinkOnClick={() => {
        unsaveProjectMutation.mutate({
          params: {
            project_id: project.id,
          },
        });
      }}
      userOrOrg='organization'
      dataTestId={`${dataTestids.MY_PROJECTS.PROJECT_CARD.CONTAINER}-${project.id}`}
    />
  );
};

const CardContent = (project: ProCardProject) => {
  return !project?.name ?
      <div className='flex justify-left gap-2 items-center text-md'>
        {`Custom project`}
        <div className='hidden md:inline mt-2'>
          <AlertIconTooltip
            size='md'
            placement='bottom end'
            header="What's a custom project?"
            supportingText='This organization is interested in collaborating with you to create a project that is a good fit for both of you. If you connect, you can discuss more of these details in your discovery call. Once you match, you can update the project name.'
          />
        </div>
      </div>
    : <div>{project?.name}</div>;
};
